/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { EventDispatchManager } from '../EventDispatchManager';
import {
  Action,
  Player,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  PLAYBACK_STATES,
} from '@quickplay/player-interface';
import { ChangeType } from '../model/ChangeType';
import { Logger } from '@quickplay/foundation';
import { EventInfo } from '../model/EventInfo';

/**
 * Periodic time observer in milliseconds
 */
const PERIODIC_INTERVAL = 1000;

export class DefaultEventDispatchManager implements EventDispatchManager {
  private timeObserverToken: any;
  private streamStartTime: number | undefined = undefined;
  private nearestUpcomingEvent: number | undefined = undefined;
  private upcomingEvents: Map<string, any> | undefined = undefined;
  private player_: Player;
  private spoilerFlag_: boolean;
  private logger_: Logger;

  constructor(spoilerFlag: boolean, player: Player, logger: Logger) {
    this.player_ = player;
    this.spoilerFlag_ = spoilerFlag;
    this.logger_ = logger;
  }

  eventMetadata(document: EventInfo): void {
    if (document.scheduledStart) {
      this.streamStartTime = new Date(document.scheduledStart).getTime() / 1000;
    }

    this.player_.send(StreamTimelineEvent.GAME_INFO, Action.NONE, document);
  }

  eventReceived(type: ChangeType, documentId: string, document: Map<string, any>): void {
    if (this.shouldDispatchImmediately(type, documentId)) {
      this.sendEvent(document, type);
    } else {
      switch (type) {
        case ChangeType.ADDED:
        case ChangeType.MODIFIED: {
          this.addToUpcomingEvents(documentId, document);
          break;
        }
        case ChangeType.DELETED: {
          this.removeFromUpcomingEventsFromDocumentId(documentId);
          break;
        }
      }
    }
  }

  updatePlayer(player: Player): void {
    this.player_ = player;
    this.addPeriodicTimeObserver();
  }

  /**
   * Determines whether the event should be dispatched immediately or not.
   * @param type
   * @param eventTimestamp
   * @private
   */
  private shouldDispatchImmediately(type: ChangeType, eventTimestamp: string): boolean {
    if (this.spoilerFlag_) {
      return true;
    }

    switch (type) {
      case ChangeType.ADDED: {
        const eventEpoch = this.getEpoch(eventTimestamp);

        if (eventEpoch >= 0) {
          return this.canSendEvent(eventEpoch);
        }

        return false;
      }
      case ChangeType.MODIFIED:
      case ChangeType.DELETED: {
        return !this.upcomingEvents || !this.upcomingEvents.has(eventTimestamp);
      }
    }

    return false;
  }

  /**
   * Sends captured future events on the appropriate time based on player playhead.
   * Case 1:
   *  When:
   *    - The captured `nearestUpcomingEvent` is lessthen the playhead.
   *  Then:
   *    - Filter the `upcomingEvents` which is less then the playhead position.
   *    - Emit those filtered events one by one.
   * Case 2:
   *  When:
   *    - The captured `nearestUpcomingEvent` is not lessthen the playhead.
   *  Then:
   *    - Don't process the `upcomingEvents` list. Because the playhead not croses an event from the `upcomingEvents`
   *    list `nearestUpcomingEvent` (state object for the list).
   * @private
   */
  private addPeriodicTimeObserver(): void {
    // don't add a new periodic time observer if there's one active
    if (this.timeObserverToken) {
      return;
    }

    // notify every second
    this.timeObserverToken = setInterval(() => {
      const events = this.fetchUnDispatchedEligibleEvents();

      if (events && events.size > 0) {
        this.sendEventsFromList(events);
      }
    }, PERIODIC_INTERVAL);
  }

  /**
   * Removes periodic time observer on situations where it is not needed.
   * This should be called after removing all items from `upcomingEvents` list.
   * @private
   */
  private removePeriodicTimeObserver(): void {
    if (this.timeObserverToken) {
      clearInterval(this.timeObserverToken);

      this.timeObserverToken = undefined;
    }
  }

  /**
   * Function to determine `upcomingEvents` have events to dispatch for the curent playhead position or not.
   * @private
   */
  private shouldDispatchFutureEvent(): boolean {
    return !this.nearestUpcomingEvent ? false : this.canSendEvent(this.nearestUpcomingEvent);
  }

  /**
   * Filters `upcomingEvents` events to dispatch.
   * @private
   */
  private fetchUnDispatchedEligibleEvents(): Map<string, any> | null {
    if (!this.shouldDispatchFutureEvent()) {
      return null;
    }

    // Developer Notes:
    // As periodicTimeObserver will dispatch the events periodically based on the playerTime and eventEpoch.
    // when switching between live modes existing player has been stopped and new player is created. Before the updation of new player instance, the existing
    // player state goes to idle state and goes to VOD and player currentTime is non-zero i.e, player currentTime is greater or lesser or equal to eventEpoch. In this case
    // the streamTimelineEvents should not be dispatched untill the new player instance is get updated.
    if (this.player_.playerState === PLAYBACK_STATES.IDLE) {
      this.removePeriodicTimeObserver();
      return null;
    }

    const unDispatchedEligibleEvents = new Map<string, any>();

    if (this.upcomingEvents) {
      Array.from(this.upcomingEvents.keys()).forEach((key: string) => {
        const eventEpoch = this.getEpoch(key);

        if (this.canSendEvent(eventEpoch)) {
          unDispatchedEligibleEvents.set(key, this.upcomingEvents?.get(key));
        }
      });
    }

    return unDispatchedEligibleEvents;
  }

  /**
   * Checks the player epoch time against actual event timestamp.
   * @param eventEpoch
   * @private
   */
  private canSendEvent(eventEpoch: number): boolean {
    const playerEpoch = this.player_.isLive
      ? this.player_.currentEpochTime
      : this.getComputedEpoch(this.player_.currentTime);

    return playerEpoch >= eventEpoch;
  }

  /**
   * Sends bulk of new events to the client. Once an event is sent from `upcomingEvents` it should
   * be removed from the list.
   * @param recentEvents
   * @private
   */
  private sendEventsFromList(recentEvents: Map<string, any>): void {
    recentEvents.forEach((metadata: Map<string, any>) => {
      this.sendEvent(metadata, ChangeType.ADDED);
    });

    this.removeFromUpcomingEventsFromDocumentMap(recentEvents);
  }

  /**
   * Sends a new, modified or deleted event to the player.
   * @param metadata
   * @param type
   * @private
   */
  private sendEvent(metadata: Map<string, any>, type: ChangeType): void {
    this.logger_.debug(`[STREAM_TIMELINE] Dispatching ${type} event`, metadata);

    this.player_.send(StreamTimelineEvent.GAME_EVENT, Action.NONE, this.composeMetadata(metadata, type));
  }

  /**
   * Adds an event to the `upcomingEvents` list to send it later.
   *
   * If the added event is nearest future event then we should update the
   * `nearestUpcomingEvent` value.
   *
   * Once a item added to the `upcomingEvents` list, we need to add periodic observer to
   * send later. If it is already added by other item, we can ignore.
   *
   * @param documentId
   * @param document
   * @private
   */
  private addToUpcomingEvents(documentId: string, document: any): void {
    // if upcomingEvents is undefined then create it
    if (!this.upcomingEvents) {
      this.upcomingEvents = new Map<string, any>();
    }

    this.upcomingEvents.set(documentId, document);

    this.updateNearestFutureEvent(documentId);

    this.addPeriodicTimeObserver();
  }

  /**
   * Removes sent events from `upcomingEvents`.
   *
   * Once the item is removed, the periodic time observer is removed too if the list is empty. So we
   * can avoid periodical check when there is no data on `upcomingEvents`.
   *
   * Update the `nearestUpcomingEvent` with the nearest feature event from the
   * `upcomingEvents` list.
   *
   * @param documentId
   * @private
   */
  private removeFromUpcomingEventsFromDocumentId(documentId: string): void {
    this.upcomingEvents?.delete(documentId);

    if (this.upcomingEvents?.size === 0) {
      this.removePeriodicTimeObserver();
    }

    this.updateNearestFutureEventFromUnsentEvents();
  }

  /**
   * Removes any sent event from the `upcomingEvents` list.
   *
   * Once the item is removed, the periodic time observer is removed too if the list is empty. So we
   * can avoid periodical check when there is no data on `upcomingEvents`.
   *
   * Update the `nearestUpcomingEvent` with the nearest feature event from the
   * `upcomingEvents` list.
   *
   * @param sendEvents
   * @private
   */
  private removeFromUpcomingEventsFromDocumentMap(sendEvents: Map<string, any>): void {
    Array.from(sendEvents.keys()).forEach((key: string) => {
      this.upcomingEvents?.delete(key);
    });

    if (this.upcomingEvents?.size === 0) {
      this.removePeriodicTimeObserver();
    }

    this.updateNearestFutureEventFromUnsentEvents();
  }

  /**
   * Function to check update the `nearestUpcomingEvent` if the new event is the most nearest to send.
   *
   * @param documentId
   * @private
   */
  private updateNearestFutureEvent(documentId: string): void {
    let newEvent: number | undefined = this.getEpoch(documentId);

    newEvent = isNaN(newEvent) ? undefined : newEvent;

    if (this.nearestUpcomingEvent) {
      if (newEvent && newEvent < this.nearestUpcomingEvent) {
        this.nearestUpcomingEvent = newEvent;
      }
    } else {
      this.nearestUpcomingEvent = newEvent;
    }
  }

  /**
   * Updates `nearestUpcomingEvent` .
   *
   * When one or more items removed from `upcomingEvents` we should update the value to keep updated.
   *
   * @private
   */
  private updateNearestFutureEventFromUnsentEvents(): void {
    this.nearestUpcomingEvent = undefined;

    if (this.upcomingEvents) {
      Array.from(this.upcomingEvents.keys()).forEach((documentId: string) => {
        this.updateNearestFutureEvent(documentId);
      });
    }
  }

  /**
   * Composes a `StreamTimelineMetadata` object with the event.
   *
   * @param eventMetadata
   * @param type
   * @private
   */
  private composeMetadata(eventMetadata: Map<string, any>, type: ChangeType): StreamTimelineMetadata {
    return {
      eventMetadata: eventMetadata,
      type: type,
    };
  }

  /**
   * Calculates epoch time for the given playhead position in milliseconds.
   * @param playhead
   * @private
   */
  private getComputedEpoch(playhead: number): number {
    return this.streamStartTime ? this.streamStartTime + playhead : playhead;
  }

  /**
   * Converts timestamp string to epoch
   *
   * @param timestamp
   * @private
   */
  private getEpoch(timestamp: string): number {
    return new Date(timestamp).getTime() / 1000;
  }
}
